export interface EmailData {
	name: string;
	email: string;
	phone: string;
	company?: string;
	project: string;
	message: string;
}

// EmailJS configuration
const EMAILJS_CONFIG = {
	serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || "",
	templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "",
	userId: process.env.REACT_APP_EMAILJS_USER_ID || "",
};

// Postmark configuration (for server-side use only)
const POSTMARK_CONFIG = {
	apiKey: process.env.REACT_APP_POSTMARK_API_KEY || "",
	endpoint: "https://api.postmarkapp.com/email",
};

/**
 * Send email using EmailJS (Frontend-friendly)
 * This can connect to various email services including Postmark
 */
export const sendEmailWithEmailJS = async (data: EmailData): Promise<void> => {
	if (!window.emailjs) {
		throw new Error(
			"EmailJS is not loaded. Please include the EmailJS script in your HTML."
		);
	}

	if (
		!EMAILJS_CONFIG.serviceId ||
		!EMAILJS_CONFIG.templateId ||
		!EMAILJS_CONFIG.userId
	) {
		throw new Error(
			"EmailJS configuration is incomplete. Please check your environment variables."
		);
	}

	const templateParams = {
		from_name: data.name,
		from_email: data.email,
		from_phone: data.phone,
		company: data.company || "Not specified",
		project_type: data.project,
		message: data.message,
		to_email: "miguel@govaengineering.com",
		reply_to: data.email,
	};

	try {
		const result = await window.emailjs.send(
			EMAILJS_CONFIG.serviceId,
			EMAILJS_CONFIG.templateId,
			templateParams,
			EMAILJS_CONFIG.userId
		);
		console.log("EmailJS email sent successfully:", result);
	} catch (error) {
		console.error("EmailJS error:", error);
		throw new Error("Failed to send email via EmailJS");
	}
};

/**
 * Send email using Postmark directly (Server-side only - will fail in browser due to CORS)
 */
export const sendEmailWithPostmark = async (data: EmailData): Promise<void> => {
	if (!POSTMARK_CONFIG.apiKey) {
		throw new Error(
			"Postmark API key is not configured. Please check your environment variables."
		);
	}

	const emailData = {
		From: "miguel@govaengineering.com",
		To: "miguel@govaengineering.com",
		ReplyTo: data.email,
		Subject: `New Contact Form Submission: ${data.project}`,
		HtmlBody: `
			<h2>New Contact Form Submission</h2>
			<p><strong>Name:</strong> ${data.name}</p>
			<p><strong>Email:</strong> ${data.email}</p>
			<p><strong>Phone:</strong> ${data.phone}</p>
			<p><strong>Company:</strong> ${data.company || "Not specified"}</p>
			<p><strong>Project Type:</strong> ${data.project}</p>
			<p><strong>Message:</strong></p>
			<p>${data.message.replace(/\n/g, "<br>")}</p>
		`,
		TextBody: `
			New Contact Form Submission
			
			Name: ${data.name}
			Email: ${data.email}
			Phone: ${data.phone}
			Company: ${data.company || "Not specified"}
			Project Type: ${data.project}
			Message: ${data.message}
		`,
		MessageStream: "outbound",
	};

	try {
		const response = await fetch(POSTMARK_CONFIG.endpoint, {
			method: "POST",
			headers: {
				"X-Postmark-Server-Token": POSTMARK_CONFIG.apiKey,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(emailData),
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(
				`Postmark error: ${errorData.Message || "Unknown error"}`
			);
		}

		const result = await response.json();
		console.log("Postmark email sent successfully:", result);
	} catch (error) {
		console.error("Postmark error:", error);
		throw new Error("Failed to send email via Postmark");
	}
};

/**
 * Main email sending function - defaults to EmailJS for frontend compatibility
 */
export const sendContactEmail = async (data: EmailData): Promise<void> => {
	// Try EmailJS first (frontend-friendly, can connect to Postmark)
	if (
		EMAILJS_CONFIG.serviceId &&
		EMAILJS_CONFIG.templateId &&
		EMAILJS_CONFIG.userId
	) {
		await sendEmailWithEmailJS(data);
		return;
	}

	// Fallback to direct Postmark (will fail in browser due to CORS)
	await sendEmailWithPostmark(data);
};

// Type declarations
declare global {
	interface Window {
		emailjs: {
			send: (
				serviceId: string,
				templateId: string,
				templateParams: any,
				userId: string
			) => Promise<any>;
		};
		gtag?: (...args: any[]) => void;
	}
}
